import React, { useState, useEffect } from 'react';
import { MapPin, Plus, Pencil, Trash2, GripVertical, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { getBranchAuthHeaders } from '@/utils/branchApi';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function KitchenStationsPage() {
  const [stations, setStations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPrintSettingsOpen, setIsPrintSettingsOpen] = useState(false);
  const [editingStation, setEditingStation] = useState(null);
  const [selectedStation, setSelectedStation] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    sort_order: 0,
    enabled: true
  });
  const [printSettings, setPrintSettings] = useState({
    print_copies: 1,
    group_mode: 'NONE',
    sort_mode: 'TIME_ADDED',
    cut_paper: false,
    beep: false,
    template_id: ''
  });

  const fetchStations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/kitchen/stations`, {
        headers: getBranchAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setStations(data);
      }
    } catch (err) {
      toast.error('Failed to load stations');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/kitchen/templates?status=ACTIVE`, {
        headers: getBranchAuthHeaders()
      });
      if (res.ok) {
        setTemplates(await res.json());
      }
    } catch (err) {
      console.error('Failed to load templates');
    }
  };

  useEffect(() => {
    fetchStations();
    fetchTemplates();
  }, []);

  const openAddModal = () => {
    setEditingStation(null);
    setFormData({
      code: '',
      name: '',
      sort_order: stations.length,
      enabled: true
    });
    setIsModalOpen(true);
  };

  const openEditModal = (station) => {
    setEditingStation(station);
    setFormData({
      code: station.code,
      name: station.name,
      sort_order: station.sort_order,
      enabled: station.enabled
    });
    setIsModalOpen(true);
  };

  const openPrintSettings = (station) => {
    setSelectedStation(station);
    setPrintSettings({
      print_copies: station.print_copies || 1,
      group_mode: station.group_mode || 'NONE',
      sort_mode: station.sort_mode || 'TIME_ADDED',
      cut_paper: station.cut_paper || false,
      beep: station.beep || false,
      template_id: station.template_id || ''
    });
    setIsPrintSettingsOpen(true);
  };

  const savePrintSettings = async () => {
    if (!selectedStation) return;

    try {
      const res = await fetch(`${API_URL}/api/kitchen/stations/${selectedStation.id}/print-settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getBranchAuthHeaders()
        },
        body: JSON.stringify({
          ...printSettings,
          template_id: printSettings.template_id || null
        })
      });

      if (res.ok) {
        toast.success('Print settings updated');
        setIsPrintSettingsOpen(false);
        fetchStations();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (err) {
      toast.error('Error saving print settings');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingStation 
        ? `${API_URL}/api/kitchen/stations/${editingStation.id}`
        : `${API_URL}/api/kitchen/stations`;
      
      const method = editingStation ? 'PATCH' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getBranchAuthHeaders()
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success(editingStation ? 'Station updated' : 'Station created');
        setIsModalOpen(false);
        fetchStations();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save station');
      }
    } catch (err) {
      toast.error('Error saving station');
    }
  };

  const deleteStation = async (stationId) => {
    if (!window.confirm('Are you sure you want to delete this station?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/kitchen/stations/${stationId}`, {
        method: 'DELETE',
        headers: getBranchAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Station deleted');
        fetchStations();
      } else {
        toast.error('Failed to delete station');
      }
    } catch (err) {
      toast.error('Error deleting station');
    }
  };

  const toggleEnabled = async (station) => {
    try {
      const res = await fetch(`${API_URL}/api/kitchen/stations/${station.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getBranchAuthHeaders()
        },
        body: JSON.stringify({ enabled: !station.enabled })
      });
      
      if (res.ok) {
        toast.success(`Station ${!station.enabled ? 'enabled' : 'disabled'}`);
        fetchStations();
      }
    } catch (err) {
      toast.error('Error updating station');
    }
  };

  // Common station presets
  const presets = [
    { code: 'GRILL', name: 'Grill Station' },
    { code: 'FRY', name: 'Fry Station' },
    { code: 'SAUTE', name: 'Sauté Station' },
    { code: 'SALAD', name: 'Salad/Cold Station' },
    { code: 'BAR', name: 'Bar' },
    { code: 'DESSERT', name: 'Dessert Station' },
    { code: 'EXPO', name: 'Expo' }
  ];

  const applyPreset = (preset) => {
    setFormData({
      ...formData,
      code: preset.code,
      name: preset.name
    });
  };

  return (
    <div data-testid="kitchen-stations-page" className="p-6 dark:bg-[#011627]">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 dark:text-[#d6deeb]">
            <MapPin className="w-6 h-6" /> Kitchen Stations
          </h1>
          <p className="text-muted-foreground dark:text-[#7fdbca]">Define kitchen areas for routing tickets</p>
        </div>
        <Button onClick={openAddModal} data-testid="add-station-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Station
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-10">Loading...</div>
      ) : stations.length === 0 ? (
        <Card>
          <CardContent className="text-center py-10">
            <MapPin className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium">No stations configured</h3>
            <p className="text-muted-foreground mb-4">
              Add kitchen stations to organize ticket routing
            </p>
            <Button onClick={openAddModal}>
              <Plus className="w-4 h-4 mr-2" /> Add Station
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {stations.map((station, idx) => (
            <Card key={station.id} data-testid={`station-card-${station.code}`} className="dark:bg-[#01111d] dark:border-[#1d3b53]">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <GripVertical className="w-5 h-5 text-muted-foreground dark:text-[#5f7e97] cursor-grab" />
                    <div>
                      <div className="font-medium flex items-center gap-2 dark:text-[#d6deeb]">
                        {station.name}
                        <Badge variant="outline" className="dark:border-[#1d3b53] dark:text-[#7fdbca]">{station.code}</Badge>
                      </div>
                      <div className="text-sm text-muted-foreground dark:text-[#5f7e97]">
                        Order: {station.sort_order}
                        {station.print_copies > 1 && ` • ${station.print_copies} copies`}
                        {station.beep && ' • Beep'}
                        {station.cut_paper && ' • Cut'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={station.enabled ? 'default' : 'secondary'} className="dark:bg-[#82aaff] dark:text-[#011627]">
                      {station.enabled ? 'Active' : 'Disabled'}
                    </Badge>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => openPrintSettings(station)}
                      className="dark:border-[#1d3b53] dark:text-[#d6deeb] dark:hover:bg-[#1d3b53]"
                      title="Print Settings"
                    >
                      <Settings2 className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => openEditModal(station)}
                      className="dark:border-[#1d3b53] dark:text-[#d6deeb] dark:hover:bg-[#1d3b53]"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => toggleEnabled(station)}
                      className="dark:border-[#1d3b53] dark:text-[#d6deeb] dark:hover:bg-[#1d3b53]"
                    >
                      {station.enabled ? 'Disable' : 'Enable'}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-red-500 dark:text-[#ff5874]"
                      onClick={() => deleteStation(station.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="dark:bg-[#01111d] dark:border-[#1d3b53]">
          <DialogHeader>
            <DialogTitle className="dark:text-[#d6deeb]">
              {editingStation ? 'Edit Station' : 'Add Station'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {/* Presets */}
              {!editingStation && (
                <div>
                  <Label className="dark:text-[#d6deeb]">Quick Presets</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {presets.map(preset => (
                      <Button
                        key={preset.code}
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => applyPreset(preset)}
                        className="dark:border-[#1d3b53] dark:text-[#d6deeb] dark:hover:bg-[#1d3b53]"
                      >
                        {preset.name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              
              <div>
                <Label className="dark:text-[#d6deeb]">Station Code</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  placeholder="GRILL"
                  required
                  maxLength={32}
                  className="dark:bg-[#011627] dark:border-[#1d3b53] dark:text-[#d6deeb]"
                />
                <p className="text-sm text-muted-foreground dark:text-[#5f7e97] mt-1">
                  Short identifier (e.g., GRILL, FRY, BAR)
                </p>
              </div>
              
              <div>
                <Label className="dark:text-[#d6deeb]">Station Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Grill Station"
                  required
                  className="dark:bg-[#011627] dark:border-[#1d3b53] dark:text-[#d6deeb]"
                />
              </div>
              
              <div>
                <Label className="dark:text-[#d6deeb]">Sort Order</Label>
                <Input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value)})}
                  className="dark:bg-[#011627] dark:border-[#1d3b53] dark:text-[#d6deeb]"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label className="dark:text-[#d6deeb]">Enabled</Label>
                <Switch
                  checked={formData.enabled}
                  onCheckedChange={(checked) => setFormData({...formData, enabled: checked})}
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsModalOpen(false)}
                className="dark:border-[#1d3b53] dark:text-[#d6deeb] dark:hover:bg-[#1d3b53]"
              >
                Cancel
              </Button>
              <Button type="submit">
                {editingStation ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* P3: Print Settings Modal */}
      <Dialog open={isPrintSettingsOpen} onOpenChange={setIsPrintSettingsOpen}>
        <DialogContent className="dark:bg-[#01111d] dark:border-[#1d3b53]">
          <DialogHeader>
            <DialogTitle className="dark:text-[#d6deeb]">
              Print Settings - {selectedStation?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="dark:text-[#d6deeb]">Copies</Label>
              <Select
                value={String(printSettings.print_copies)}
                onValueChange={(val) => setPrintSettings({...printSettings, print_copies: parseInt(val)})}
              >
                <SelectTrigger className="dark:bg-[#011627] dark:border-[#1d3b53] dark:text-[#d6deeb]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-[#01111d] dark:border-[#1d3b53]">
                  {[1, 2, 3, 4, 5].map(n => (
                    <SelectItem key={n} value={String(n)} className="dark:text-[#d6deeb] dark:focus:bg-[#1d3b53]">
                      {n} {n === 1 ? 'copy' : 'copies'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="dark:text-[#d6deeb]">Group Mode</Label>
              <Select
                value={printSettings.group_mode}
                onValueChange={(val) => setPrintSettings({...printSettings, group_mode: val})}
              >
                <SelectTrigger className="dark:bg-[#011627] dark:border-[#1d3b53] dark:text-[#d6deeb]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-[#01111d] dark:border-[#1d3b53]">
                  <SelectItem value="NONE" className="dark:text-[#d6deeb] dark:focus:bg-[#1d3b53]">None</SelectItem>
                  <SelectItem value="CATEGORY" className="dark:text-[#d6deeb] dark:focus:bg-[#1d3b53]">By Category</SelectItem>
                  <SelectItem value="COURSE" className="dark:text-[#d6deeb] dark:focus:bg-[#1d3b53]">By Course</SelectItem>
                  <SelectItem value="SEAT" className="dark:text-[#d6deeb] dark:focus:bg-[#1d3b53]">By Seat</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="dark:text-[#d6deeb]">Sort Mode</Label>
              <Select
                value={printSettings.sort_mode}
                onValueChange={(val) => setPrintSettings({...printSettings, sort_mode: val})}
              >
                <SelectTrigger className="dark:bg-[#011627] dark:border-[#1d3b53] dark:text-[#d6deeb]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-[#01111d] dark:border-[#1d3b53]">
                  <SelectItem value="TIME_ADDED" className="dark:text-[#d6deeb] dark:focus:bg-[#1d3b53]">Time Added</SelectItem>
                  <SelectItem value="MENU_ORDER" className="dark:text-[#d6deeb] dark:focus:bg-[#1d3b53]">Menu Order</SelectItem>
                  <SelectItem value="CATEGORY" className="dark:text-[#d6deeb] dark:focus:bg-[#1d3b53]">Category</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="dark:text-[#d6deeb]">Template</Label>
              <Select
                value={printSettings.template_id || 'none'}
                onValueChange={(val) => setPrintSettings({...printSettings, template_id: val === 'none' ? '' : val})}
              >
                <SelectTrigger className="dark:bg-[#011627] dark:border-[#1d3b53] dark:text-[#d6deeb]">
                  <SelectValue placeholder="Use Default" />
                </SelectTrigger>
                <SelectContent className="dark:bg-[#01111d] dark:border-[#1d3b53]">
                  <SelectItem value="none" className="dark:text-[#d6deeb] dark:focus:bg-[#1d3b53]">Use Default</SelectItem>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id} className="dark:text-[#d6deeb] dark:focus:bg-[#1d3b53]">
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <Label className="dark:text-[#d6deeb]">Cut Paper Between Copies</Label>
              <Switch
                checked={printSettings.cut_paper}
                onCheckedChange={(checked) => setPrintSettings({...printSettings, cut_paper: checked})}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label className="dark:text-[#d6deeb]">Beep on Print</Label>
              <Switch
                checked={printSettings.beep}
                onCheckedChange={(checked) => setPrintSettings({...printSettings, beep: checked})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setIsPrintSettingsOpen(false)}
              className="dark:border-[#1d3b53] dark:text-[#d6deeb] dark:hover:bg-[#1d3b53]"
            >
              Cancel
            </Button>
            <Button onClick={savePrintSettings}>
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
